﻿import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { SetFiltering } from 'state/actions';
import { UserAccess, UserHasAccess } from "components/user-access";
import { PageRestricted } from "pages/errors/page-restricted";
import { FormDatePeriodSelector, FormLocationSelector, FormTextInput } from "components/form-fields"
import { Tooltip } from "components/tooltip";
import { Button, LinkButton } from "components/buttons"
import { Filter, FilterButton } from "components/filtering";
import { NotificationToaster, NotificationInline } from "components/notifications";
import { Intent, Position } from "@blueprintjs/core";
import { ListingPagination, ListingTable } from "components/listing";
import { AssetService, FleetService, LinkService, LocationService } from 'services';
import { ListingKeyBlock } from "components/key";
import { Numeric } from "components/formatting/Numeric";
import { DateToLocal } from "components/dates/DateToLocal";
import { PreferencesModal } from "components/preferences-modal/PreferencesModal";
import { AssetStatus } from 'components/status/AssetStatus';
import { useDebounce } from "hooks/useDebounce";
import { InternalLink, Tabs } from 'components/navigation';
import { AssetTypeFilterRenderer } from "pages/vehicle/asset-type-filter-renderer/AssetTypeFilterRenderer";
import classNames from "classnames";
import moment from "moment";
import axios from 'axios';
import "pages/vehicle/Vehicle.css"
import { KeyModal } from "pages/vehicle/key-modal";
import "pages/fleet/Fleet.css";
import { ShowHide } from "components/layout";
import { FleetDayDetailModal } from "pages/fleet";
import { ReportList } from "components/reporting/ReportList";
import { AssetHelperFunctions } from 'helpers';

export function Fleet() {
    const requiredActions = ["FMS:View"];
    const requiredAssetCreateActions = ["FMS:Edit"];
    const canAccessPage = UserHasAccess(requiredActions);
    const canAccessCreateAsset = UserHasAccess(requiredAssetCreateActions);
    const firstColumnLabel = "ID/Registration";
    const allowedAdditionalSortableHeaders = ["Asset Location", "Fleet Number", "Next Event Date"];
    const calculatedColumnNames = ["KM without card"];

    const sortTableHeadersPrefix = ["ID/Registration", ""];
    const singleClickDelay = 250;
    const clickTimerRef = useRef(null);
    const suppressSingleClickRef = useRef(false);

    const includeHiddenFilter = { key: "IncludeHidden", value: "false" };
    const periodSelectorDateFormat = "DD MMM YYYY";
    const defaultItemsPerPage = 25;
    const daysInWeek = 7;
    const saturdayInt = 6;
    const sundayInt = 7;
    const cellWidthIdentifier = 175;
    const cellWidthDefault = 100;
    const cellWidthDay = 50;
    const cellWidthWeek = 130;
    const cellWidthMonth = 130;
    const debounceTimeout = 750;
    const filterName = "fleetAssets";
    const filterSearchSelector = "fleetSearch";

    const maxMonthIcons = 10;
    const maxWeekIcons = 5;

    const longMessageMilliseconds = 3500;

    const weekendDays = [saturdayInt, sundayInt];
    const [expandedAssetTypes, setExpandedAssetTypes] = useState({});
    const [isInitialising, setIsInitialising] = useState(true);
    const fleetSearchRedux = useSelector(state => state.filters[filterSearchSelector]);

    const [isSearching, setIsSearching] = useState(true);
    const [locationsLoading, setLocationsLoading] = useState(true);
    const [useLocationDefaults, setUseLocationDefaults] = useState(null);
    const [locationList, setLocationList] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);

    const [showFilters, setShowFilters] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState([]);
    const [pageFilters, setPageFilters] = useState([]);
    const [selectedLocations, setSelectedLocations] = useState(null);
    const [useHierarchy, setUseHierarchy] = useState(true);
    const inputSearchRef = useRef(null);
    const initialSelectedTab = fleetSearchRedux?.selectedTab ?? 0;
    const [selectedTab, setSelectedTab] = useState(initialSelectedTab);
    const [tabSettings, setTabSettings] = useState(getTabSettings(initialSelectedTab));

    const [startDate, setStartDate] = useState(null);
    const [startDateDayWeek, setStartDateDayWeek] = useState(null);
    const [startDateMonth, setStartDateMonth] = useState(null); 

    const refOverflowTable = useRef();
    const refHeaderTable = useRef();
    const refHeaderScrollListener = useRef();
    const refCellOverflow = useRef([]);
    const refHeaderOverflow = useRef([]);

    const [assetStatusesMappings, setAssetStatusesMappings] = useState([]);
    const [ownershipAttributeMappings, setOwnershipAttributeMappings] = useState([]);
    const [eventTypeMappings, setEventTypeMappings] = useState([]);
    const [eventStatusesMappings, setEventStatusesMappings] = useState([]);
    const [eventOptions, setEventOptions] = useState({});
    const [fleetSearchReduxLoaded, setFleetSearchReduxLoaded] = useState(false);
    const [recalcPeriodWithDateMonth, setRecalcPeriodWithDateMonth] = useState(null);
    const [recalcPeriodWithDateWeek, setRecalcPeriodWithDateWeek] = useState(null);

    const currentSearchTerm = useRef();
    const currentPageNo = useRef();
    const currentSelectedTab = useRef();
    const currentStartDate = useRef();
    const currentEndDate = useRef();
    const currentTableSortBy = useRef();
    const currentTableSortDir = useRef();
    const currentAppliedFilters = useRef();
    const currentLocations = useRef();
    
    // Key Modal
    const [showKeyModal, setShowKeyModal] = useState(false);

    // Listings objects:
    const [searchResult, setSearchResult] = useState([]);
    const [searchResultData, setSearchResultData] = useState(null);
    const [assetsWithEventWarnings, setAssetsWithEventWarnings] = useState(null);
    const [assetsWithNotes, setAssetsWithNotes] = useState([]); 
    const [assetEvents, setAssetEvents] = useState(null);
    const [activeHeaders, setActiveHeaders] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [sortedBy, setSortedBy] = useState(firstColumnLabel);
    const [sortedDir, setSortedDir] = useState("A");
    const [sortableHeaders, setSortableHeaders] = useState([firstColumnLabel]);
    const [currentPageNumber, setCurrentPageNumber] = useState(1);
    const perPage = defaultItemsPerPage;
    const [columnClasses, setColumnClasses] = useState([]);
    const [columnWidths, setColumnWidths] = useState([cellWidthIdentifier, cellWidthDefault]);

    // Day Detail Modal
    const [accountEventTypes, setAccountEventTypes] = useState([]);
    const [showDayDetailModal, setShowDayDetailModal] = useState(false);
    const [dayDetailModalAsset, setDayDetailModalAsset] = useState({});
    const [dayDetailPeriodDetails, setDayDetailPeriodDetails] = useState({
        periodType: "day",
        startDate: ""
    });

    // DisplayPreferencesModal
    const [displayPreferences, setDisplayPreferences] = useState([]);
    const [showPreferencesModal, setShowPreferencesModal] = useState(false);

    const [timeUnitsToShow, setTimeUnitsToShow] = useState(4);
    const [preferencesUpdated, setPreferencesUpdated] = useState(false);
    const [locationSelectorOpen, setLocationSelectorOpen] = useState(false);
    const [tilesToShow, setTilesToShow] = useState(daysInWeek * timeUnitsToShow);

    //Reports
    const [reportDownloading, setReportDownloading] = useState(false);
    const [downloadingReportName, setDownloadingReportName] = useState(null);
    const [lastSearchPayload, setLastSearchPayload] = useState(null);
    let hideBannerTimeout = null;


    const handleSavePreferences = (newPreferences, newTimeUnitsToShow) => {
        setDisplayPreferences(newPreferences);
        setTimeUnitsToShow(newTimeUnitsToShow);
        setPreferencesUpdated(true);
    };

    useEffect(() => {
        let newTilesToShow = 1;
        const multiplier = 3;
        const addition = 5;

        if (selectedTab === 0) { // Days
            newTilesToShow = timeUnitsToShow * daysInWeek;
        } else if (selectedTab === 1) { // Weeks
            newTilesToShow = (multiplier * (timeUnitsToShow - 1) + addition);
        } else if (selectedTab === 2) { // Months
            newTilesToShow = (multiplier * (timeUnitsToShow - 1) + addition);
        }

        let newPeriodsToShow = selectedTab === 0 ? timeUnitsToShow : tilesToShow;

        //We set startDate and periodsToShow at the same time as individual updates causes the FormDatePeriodSelector to fire the onChange event at random times
        if (selectedTab === 2) {
            setRecalcPeriodWithDateMonth({ startDate: startDateMonth, periodsToShow: newPeriodsToShow });
        } else {
            setRecalcPeriodWithDateWeek({ startDate: startDateDayWeek, periodsToShow: newPeriodsToShow });
        }

        setTilesToShow(newTilesToShow);
    }, [selectedTab, timeUnitsToShow]);

    function onTabClick(tabIndex) {
        setIsSearching(true);
        //We need to stop the screen flicker
        clearScreenListing();

        setTabSettings(prev =>
            prev.map((c, idx) => ({
                ...c,
                selected: idx === tabIndex
            }))
        );
        //This won't cause the page to reload, but it being different from the currentSelectedTab should ensure when the FormDatePeriodSelector 
        //changes the date the search happens even if the date hasn't changed.
        setSelectedTab(tabIndex);
        setFleetSearchDispatch(null, null, null, null, null, null, tabIndex);
    }

    useEffect(() => {
        if (!startDate || isInitialising || locationsLoading) { return; }

        recalculateHeaders();
    }, [startDate, isInitialising, locationsLoading, displayPreferences, timeUnitsToShow, selectedTab, tilesToShow]);

    useEffect(() => {
        if (selectedLocations) {
            setLocationsLoading(false);
        }
    }, [selectedLocations]);

    useEffect(() => {
        if (displayPreferences.filter(x => x.userSelected && calculatedColumnNames.includes(x.name)).length > 0) {
            //If the user has any calculated columns displayed, we need to update them as well as the calendar items:
            performSearch();
        }
    }, [displayPreferences]);

    useEffect(() => {
        if (isInitialising || locationsLoading || !fleetSearchReduxLoaded || locationSelectorOpen || !selectedLocations || !startDate) { return; }
        performSearch();
    }, [debouncedSearchTerm, appliedFilters, selectedLocations, sortedBy, sortedDir, perPage, currentPageNumber, locationsLoading, locationSelectorOpen, isInitialising, startDate])

    useEffect(function () {
        if (!fleetSearchReduxLoaded) {
            setFleetSearchReduxLoaded(true);
            if (fleetSearchRedux) {
                setSelectedLocations(fleetSearchRedux.locations);
                setSearchTerm(fleetSearchRedux.searchTerm);
                setSortedBy(fleetSearchRedux.sortedBy);
                setCurrentPageNumber(fleetSearchRedux.currentPageNumber);
                setSortedDir(fleetSearchRedux.sortedDir);
                setShowFilters(fleetSearchRedux.showFilters);
                setUseLocationDefaults(false);
                setSelectedTab(fleetSearchRedux.selectedTab);
                setTabSettings(getTabSettings(fleetSearchRedux.selectedTab));
                // Create fresh moment instances so local state doesn't share references
                // with the Redux store (operations on moments mutate their internals).
                const startDateDayWeekMoment = moment(fleetSearchRedux.startDateDayWeek);
                const startDateMonthMoment = moment(fleetSearchRedux.startDateMonth);
                setStartDateDayWeek(startDateDayWeekMoment);
                setStartDateMonth(startDateMonthMoment);
                let stDt = fleetSearchRedux.selectedTab === 2 ? startDateMonthMoment : startDateDayWeekMoment;
                setStartDate(stDt);
            } else {
                setUseLocationDefaults(true);
                let initialDate = moment().startOf('isoWeek')
                setStartDate(initialDate);
                setStartDateDayWeek(initialDate);
                setStartDateMonth(initialDate.clone().startOf('month'));
            }
        }
    }, [fleetSearchRedux]);

    function getTabSettings(selectedTabIndex) {
        return [
            { title: 'DAYS', key: "days-tab-mmf", selected: selectedTabIndex === 0 },
            { title: 'WEEKS', key: "weeks-tab-mmf", selected: selectedTabIndex === 1 },
            { title: 'MONTHS', key: "months-tab-mmf", selected: selectedTabIndex === 2 }
        ];
    }

    function formatPageFilters(statusesForAssets, assetOwnerships, assetTypes, eventStatuses, eventTypes) {
        return [
            {
                name: "ASSETSTATUSID",
                displayName: "Status",
                items: statusesForAssets
            },
            {
                name: "ASSETOWNERSHIPID",
                displayName: "Ownership",
                items: assetOwnerships
            },
            {
                name: "ASSETTYPE",
                displayName: "Type",
                items: assetTypes
            },
            {
                name: "EVENTTYPEID",
                displayName: "Event Type",
                items: eventTypes
            },
            {
                name: "EVENTSTATUSID",
                displayName: "Event Status",
                items: eventStatuses
            },
            {
                name: "DueDateOverdue",
                displayName: "Due Date Overdue",
                singleSelect: true,
                items: boolFilterOptions
            },
            {
                name: "EventDateOverdue",
                displayName: "Event Date Overdue",
                singleSelect: true,
                items: boolFilterOptions
            }
        ];
    }

    function getViewEndExclusive(start, tab, tiles) {
        if (!start || !moment.isMoment(start)) return null;

        let s;

        switch (tab) {
            case 0:
                s = moment(start).startOf("day");
                break;
            case 1:
                s = moment(start).startOf("isoWeek");
                break;
            default:
                s = moment(start).startOf("month");
        }

        if (tab === 0) {
            // Days: tiles = number of days
            return moment(s).add(tiles, 'days').startOf('day');
        } else if (tab === 1) {
            // Weeks: tiles = number of weeks (ISO weeks)
            return moment(s).add(tiles, 'weeks').startOf('isoWeek');
        } else {
            // Months: tiles = number of months
            return moment(s).add(tiles, 'months').startOf('month');
        }
    }

    function processAssetOptions(assetOptions) {
        if (assetOptions != null) {
            setOwnershipAttributeMappings(assetOptions.assetOwnerships.map((x) => {
                return { "name": x.name, "colours": x.colour, "id": x.id, "className": x.style, "icon": x.icon }
            }));
            
            setAssetStatusesMappings(assetOptions.assetStatuses.map((x) => {
                return { "name": x.name, "colours": x.colour, "id": x.id }
            }));
        }
    }

    function processEventOptions(eventOps, eventTypes) {
        if (eventOps != null) {
            setEventStatusesMappings(eventOps.eventStatuses.map((x) => {
                return { "name": x.name, "colours": x.statusColour, "id": x.id }
            }));
        }

        if (eventTypes != null) { 
            setEventTypeMappings(eventTypes.map((x) => {
                return { "name": x.name, "id": x.id, "icon": x.iconName }
            }));
        }
    }

    function processPageFilters(assetOptions, eventOps, eventTypes) {
        let assetTypes = assetOptions.assetTypes;

        setPageFilters(formatPageFilters(assetOptions.assetStatuses, assetOptions.assetOwnerships, assetTypes, eventOps.eventStatuses, eventTypes));
    }

    function processDisplayPreferences(response) {
        if (response != null) {
            setDisplayPreferences(
                response.userDisplayOptions.map(x => {
                    return {
                        id: x.displayOptionsId,
                        name: x.name,
                        userSelected: x.userSelected,
                        userPriority: x.userPriority,
                        format: x.format,
                        minWidth: x.minWidth
                    }
                })
            );
            setTimeUnitsToShow(response.weeksToShow);
        }
    }

    useEffect(() => {
        LocationService.getFormLocationSelectorCustomLocations(['Tacho', 'Vis']).then((locations) => {
            setLocationList(locations);
        });
    }, []);

    useEffect(() => {

        const scrollHeader = () => {
            if (refHeaderTable.current != null) {
                refHeaderTable.current.style.transform = `translateX(-${refOverflowTable.current.scrollLeft}px)`;
            }
        }

        const removeExistingListeners = () => {
            refOverflowTable.current?.removeEventListener("scroll", scrollHeader);
        }

        if (refOverflowTable.current && refHeaderScrollListener.current == null) {
            //Make sure we detach any existing listeners so its not applied twice
            removeExistingListeners();
            refOverflowTable.current.addEventListener("scroll", scrollHeader);
        }

    }, [refOverflowTable.current]);

    useEffect(() => {
        if (!canAccessPage) {
            return;
        }

        dispatch({ type: 'SITE_FULL_WIDTH' });

        axios.all([
            AssetService.getAssetOptions(),
            FleetService.getEventOptions(),
            FleetService.getDisplayPreferences(),
            FleetService.searchEventTypes(1000, 1, "", "", "", [ includeHiddenFilter ]),
            FleetService.getEventTypesForAccount()
        ]).then(axios.spread(function (assetOptionsResponse, eventOptionsResponse, preferencesResponse, eventTypesResponse, accountEventTypesResponse) {
            processAssetOptions(assetOptionsResponse);
            processEventOptions(eventOptionsResponse, eventTypesResponse.data);
            processPageFilters(assetOptionsResponse, eventOptionsResponse, eventTypesResponse.data);
            processDisplayPreferences(preferencesResponse);
            setAccountEventTypes(accountEventTypesResponse);
            setEventOptions(eventOptionsResponse);
        })).catch(() => {
            NotificationToaster.show(Intent.DANGER, `Failed to load Asset. Please refresh the page.`);
        }).finally(() => {
            setIsInitialising(false);
        });
    }, []);

    useEffect(() => {
        if (preferencesUpdated) {
            performSearch(true);
            setPreferencesUpdated(false);
        }
    }, [preferencesUpdated]);


    //FILTERS:
    const boolFilterOptions = [{ name: 'Yes', displayName: 'Yes', id: true }, { name: 'No', displayName: 'No', id: false }];

    let selectedFilters = useSelector(state => state.filters[filterName] ?? []);
    const dispatch = useDispatch();

    if (selectedFilters.length === 0) {
        dispatch(SetFiltering(filterName, AssetHelperFunctions.defaultAssetStatusFilters));
    }

    function onSelectedLocationsChange(locations) {
        setSelectedLocations(locations);
        setFleetSearchDispatch(locations, null, null, null, null, null);
    }

    function onToggleFilter() {
        setShowFilters((prevState) => {
            return !prevState;
        })
    }

    function onFilterChange(listingFilters) {
        setAppliedFilters(listingFilters);
    }

    function onLocationListOpened() {
        setLocationSelectorOpen(true);
    }

    function onLocationListClose() {
        setLocationSelectorOpen(false);
    }

    //Headers Logic/Formatting:
    function recalculateHeaders() {
        if (!startDate) { return; }

        //When start date changes, update the calendar columns:
        let colWidths = [cellWidthIdentifier, cellWidthDefault];
        let calendarHeaders = [];

        if (selectedTab === 0) {
            for (let i = 0; i < (daysInWeek * timeUnitsToShow); i++) {
                let thisDay = moment(startDate).add(i, 'days');
                let thisClassNames = "";
                if (thisDay.isSame(moment(), 'day')) {
                    thisClassNames += ("current-day");
                }
                else if (weekendDays.includes(thisDay.isoWeekday())) {
                    thisClassNames += "weekend";
                }
                calendarHeaders.push(
                    <div key={`cal${thisDay.format("YYYYMMDD")}`} className={classNames(thisClassNames)}>
                        {thisDay.format("ddd")}<br />{thisDay.format("DD")}
                    </div>
                );
                colWidths.push(cellWidthDay);
            }
        } else if (selectedTab === 1) {
            for (let i = 0; i < tilesToShow; i++) {
                let weekStart = moment(startDate).add(i, 'weeks').startOf('isoWeek');
                let weekNumber = weekStart.isoWeek();
                calendarHeaders.push(
                    <div key={`week${i}`} className="calendar-week-header">
                        {weekStart.format("MMM DD")}<br />Week {weekNumber}
                    </div>
                );
                colWidths.push(cellWidthWeek);
            }
        } else if (selectedTab === 2) {
            for (let i = 0; i < tilesToShow; i++) {
                let monthStart = moment(startDate).add(i, 'months').startOf('month');
                let monthName = monthStart.format("MMM");
                let monthNumber = monthStart.month() + 1; // zero-based
                calendarHeaders.push(
                    <div key={`month${i}`} className="calendar-month-header">
                        {monthName}<br />Month {monthNumber}
                    </div>
                );
                colWidths.push(cellWidthMonth);
            }
        }
        const cellHasOverflow = (index) => {

            var cell = refHeaderOverflow.current[index];

            if (cell != null) {
                return cell.scrollWidth > cell.offsetWidth;
            }

            return false;
        }

        let formattedAdditionalHeaders = displayPreferences
            .filter(x => x.userSelected)
            .sort((x, y) => x.userPriority - y.userPriority)
            .map((x, headerIndex) => {
                colWidths.push(x.minWidth ?? cellWidthDefault);
                return (
                    <Tooltip key={x.name} content={x.name ?? ""} position="top" disabled={!cellHasOverflow(headerIndex)}>
                        <div
                            ref={(el) => {
                                refHeaderOverflow.current[headerIndex] = el;
                            }}
                            className="additional-column"
                            style={{ width: x.minWidth }}
                        >
                            {x.name}
                        </div>
                    </Tooltip>
                );
            });

        let additionalHeaderKeys = formattedAdditionalHeaders
            .filter(x => allowedAdditionalSortableHeaders.includes(x.key.toString()))
            .map((x) => { return x.key.toString() });
        
        //map the additional columns
        setActiveHeaders([...sortTableHeadersPrefix, ...calendarHeaders, ...formattedAdditionalHeaders]);
        setSortableHeaders([...sortTableHeadersPrefix, ...additionalHeaderKeys]);
        const baseColCount = sortTableHeadersPrefix.length;
        const baseColClasses = Array(baseColCount).fill("base-column");
        if (baseColCount > 1) {
            baseColClasses[1] = "asset-actions-column base-column";
        }

        setColumnClasses([
            ...baseColClasses,
            ...Array(calendarHeaders.length).fill("calendar-day"),
            ...Array(formattedAdditionalHeaders.length).fill("")
        ]);
        
        setColumnWidths(colWidths);
    }
  
    function getIdField(assetId, registrationNumber) {
        return (
            <Tooltip position={Position.TOP} content={`Asset Profile`}>
                <InternalLink link={LinkService.getVehicleProfileUrl(assetId, 'details', "area=MMF")}>{registrationNumber}</InternalLink>
            </Tooltip>
        );
    }

    function getWarningIcon(warning) {
        const tooltipText = warning.hasWarnings
            ? (
                <ul>
                    {warning?.warnings?.map(w => (<li key={w}>{w}</li>))}
                </ul>
            ) : "";

        const warningsClass = warning.hasDangerousWarnings ? "text-intent-danger" : "text-intent-warning";

        return warning.hasWarnings 
            ? (
                <div className="warning-icon">
                    <Tooltip position={Position.TOP} content={tooltipText}>
                        <i className={`fa fa-warning ${warningsClass}`} />
                    </Tooltip>
                </div>
            ) 
            : (<></>);
    }

    function suppressSingleClickAndClearTimer() {
        suppressSingleClickRef.current = true;
        if (clickTimerRef.current) {
            clearTimeout(clickTimerRef.current);
            clickTimerRef.current = null;
        }
    }

    function reloadPageFromDoubleClick(e, newTab, tileCount, periodString) {
        e.preventDefault();
        e.stopPropagation();

        suppressSingleClickAndClearTimer();
        const newStartDate = moment(startDate).add(tileCount, periodString).startOf('isoWeek');

        //This can only ever be going to the week or day view
        onWeekChange(newStartDate, null, null, true);
        onTabClick(newTab);
    }

    function getCalendarRowsItems(assetRow) {
        const assetId = assetRow.key;

        // Find the events for this asset
        const assetEventObj = assetEvents.find(ev => ev.assetId === assetId);
        const events = assetEventObj ? assetEventObj.events : [];
        const assetNotesObj = (assetsWithNotes ?? []).find(a => a.assetId === assetId);
        const notes = assetNotesObj ? assetNotesObj.notes : [];

        let rows = [];

        // Set max icons per view
        let maxIcons = 1;
        if (selectedTab === 1) maxIcons = maxWeekIcons;
        if (selectedTab === 2) maxIcons = maxMonthIcons;

        const statusColorMap = {};
        if (eventOptions && eventOptions.eventStatuses) {
            eventOptions.eventStatuses.forEach(status => {
                statusColorMap[status.id] = status.statusColour;
            });
        }

        for (let i = 0; i < tilesToShow; i++) {
            let cellDate;
            if (selectedTab === 0) {
                cellDate = moment(startDate).add(i, 'days');
            } else if (selectedTab === 1) {
                cellDate = moment(startDate).add(i, 'weeks').startOf('isoWeek');
            } else if (selectedTab === 2) {
                cellDate = moment(startDate).add(i, 'months').startOf('month');
            }

            // Find all events for this cell
            const matchingEvents = events.filter(ev => {
                if (!ev.eventDate) return false;
                const eventMoment = moment(ev.eventDate);
                if (selectedTab === 0) {
                    return eventMoment.isSame(cellDate, 'day');
                } else if (selectedTab === 1) {
                    return eventMoment.isSame(cellDate, 'isoWeek');
                } else if (selectedTab === 2) {
                    return eventMoment.isSame(cellDate, 'month');
                }
                return false;
            });

            // Find all notes for this cell
            const matchingNotes = notes.filter(n => {
                const noteDate = moment(n.date);
                if (selectedTab === 0) {
                    return noteDate.isSame(cellDate, 'day');
                } else if (selectedTab === 1) {
                    return noteDate.isSame(cellDate, 'isoWeek');
                } else if (selectedTab === 2) {
                    return noteDate.isSame(cellDate, 'month');
                }
                return false;
            });

            // VOR Range Detection
            let isVorRange = false;
            if (selectedTab === 0) {
                isVorRange = events.some(ev => {
                    if (ev.vorStartDate && ev.vorEndDate) {
                        const vorStart = moment(ev.vorStartDate);
                        const vorEnd = moment(ev.vorEndDate);
                        return cellDate.isBetween(vorStart, vorEnd, 'day', '[]');
                    }
                    return false;
                });
            }

            let onDoubleClick = null;
            if (selectedTab === 2) {
                onDoubleClick = (e) => {
                    reloadPageFromDoubleClick(e, 1, i, 'months');
                };
            }

            if (selectedTab === 1) {
                onDoubleClick = (e) => {
                    reloadPageFromDoubleClick(e, 0, i, 'weeks');
                };
            }

            const tileKey = `kb-${assetId ?? 'na'}-${selectedTab}-${i}-${startDate ? moment(startDate).format('YYYYMMDD') : 'nostart'}`;

            let keyBlockClass;
            let appliedPeriodType;

            switch (selectedTab) {
                case 0:
                    keyBlockClass = "fleet-day-view-key-block";
                    appliedPeriodType = "day";
                    break;
                case 1:
                    keyBlockClass = "fleet-week-view-key-block";
                    appliedPeriodType = "week";
                    break;
                default:
                    keyBlockClass = "fleet-month-view-key-block";
                    appliedPeriodType = "month";
            }
            
            rows.push(
                <div
                    key={tileKey}
                    className={keyBlockClass}
                    onDoubleClick={onDoubleClick}
                    onKeyDown={() => { } }
                    onClick={(e) => {
                        if (clickTimerRef.current) {
                            clearTimeout(clickTimerRef.current);
                            clickTimerRef.current = null;
                        }
                        clickTimerRef.current = setTimeout(() => {
                            if (!suppressSingleClickRef.current) {
                                displayDayDetailModal(assetRow, cellDate);
                            }
                            suppressSingleClickRef.current = false;
                            clickTimerRef.current = null;
                        }, singleClickDelay);
                    }}
                >
                    <ListingKeyBlock
                        className={keyBlockClass}
                        periodType={appliedPeriodType}
                        size={14}
                        loading={false}
                        selectable
                        colours="#fff"
                        maxIcons={maxIcons}
                        iconColour="#ccc"
                        vorRange={isVorRange}
                        events={matchingEvents}
                        notes={matchingNotes}
                    />
                </div>
            );
        }
        return rows;
    }
    
    function formatColumnDataFromSearchResult(apiData) {
        return apiData.map((x) => {
            return {
                key: x.assetId,
                status: x.assetStatus,
                warning: {
                    hasWarnings: false,
                    hasDangerousWarnings: false,
                    warnings: []
                },
                ownership: x.assetOwnership,
                assetType: {
                    ...x.assetType,
                    assetTypeSubtype: x.assetSubType
                },
                registrationNumber: x.assetRegistration,
                additionalColumns: x.columns,
                calendarItems: null
            }
        });
    }

    const additionalColumnNameMap = {
        "Asset Location": "LocationName",
        "Asset Type": "AssetTypeName",
        "Asset Type Subtype": "AssetTypeSubtypeName",
        "Fleet Number": "FleetNumber",
        "Start Date": "StartDate",
        "End Date": "EndDate",
        "Manufacturer": "Manufacturer",
        "Model": "Model",
        "Year of Manufacturer": "YearOfManufacture",
        "MOT Expiry Date": "MotExpiryDate",
        "Tax Due Date": "TaxDueDate",
        "Tachograph Calibration Date": "TachographCalibrationDate",
        "Previous Event Name": "PreviousEventName",
        "Previous Event Date": "PreviousEventDate",
        "Next Event Name": "NextEventName",
        "Next Event Date": "NextEventDate"
    };

    function mapColumnRows(row, rowIndex) {
        let baseColumns = [
            <div className="nowrap inline-items" key={`statusIcon_${row.key}`}>
                <AssetStatus statusId={row.status.id} ownershipId={row.ownership.id} isLoading={isInitialising} statusAttributeMappings={assetStatusesMappings} ownershipAttributeMappings={ownershipAttributeMappings} tooltipPosition={Position.RIGHT} />
                <div className="nowrap inline-items" key={`Warning_${row.key}`}>
                    {getWarningIcon(row.warning)}
                </div>
                {getIdField(row.key, row.registrationNumber)}
            </div>,
            <div className="nowrap inline-items" key={`Links_${row.key}`}>
                {AssetHelperFunctions.getVehicleIcon(
                    row.assetType.id,
                    row.assetType.name,
                    row.assetType.assetTypeSubtype?.name

                )}
            </div>, ...getCalendarRowsItems(row)
        ];

        let noOfBaseColumns = baseColumns.length;

        const cellHasOverflow = (cellRowIndex, cellColIndex) => {
            var rowRefs = refCellOverflow.current[cellRowIndex];

            if (rowRefs != null) {
                var cell = rowRefs[cellColIndex];
                if (cell != null) {
                    return cell.scrollWidth > cell.offsetWidth;
                }
            }

            return false;
        }

        function displayValueLookup(x) {
            let displayName = Object.keys(additionalColumnNameMap).find(key => additionalColumnNameMap[key] === x.name);
            return displayPreferences.find(y => y.name === displayName);
        }

        let additionalRows = row.additionalColumns
            .filter(x => {
                return displayValueLookup(x).userSelected;
            })
            .sort((a, b) => {
                return displayValueLookup(a).userPriority - displayValueLookup(b).userPriority;
            })
            .map((x, colIndex) => {
            if (!displayPreferences) {
                return x.value;
                }
            let preferenceItem = displayValueLookup(x);
            let format = "none";
            if (preferenceItem) {
                format = preferenceItem.format;
            }

            let value = null;

            switch (format) {
                case "DateTime":
                    value = <DateToLocal format="DD/MM/YYYY" invalidDateString="">{x.value == null ? "" : x.value}</DateToLocal>;
                    break;
                case "Int":
                    value = <Numeric>{x.value}</Numeric>;
                    break;
                default:
                    value = x.value;
                    break;
            }

            const node = <div
                ref={(el) => {
                    refCellOverflow.current[rowIndex] = refCellOverflow.current[rowIndex] || [];
                    refCellOverflow.current[rowIndex][colIndex] = el;
                }}
                className="additional-column"
                style={{ width: columnWidths[noOfBaseColumns + colIndex] }}
            >
                {value}
            </div>;

            return <Tooltip key={`col-${x.name}`} content={value == null ? "" : value} position="top" disabled={!cellHasOverflow(rowIndex, colIndex)}>{node}</Tooltip>;
        });
        return baseColumns.concat(additionalRows);
    }

    function getSearchFilters() {
        let filters = appliedFilters.map(x => {
            if (x.name === "AssetTypeSubtype" && typeof x.value === "string" && x.value.startsWith("uncat-")) {
                return { key: "AssetType", value: x.parentAssetTypeId.toString() };
            }
            return { key: x.name, value: `${x.value}` }
        });

        if (selectedLocations != null) {
            selectedLocations.forEach(function (l) {
                filters = [...filters,
                { key: "Location", value: `${l}` }
                ];
            });
        }
        return filters;
    }

    //Search logic:

    function getSearchPayload() {
        const filters = getSearchFilters();

        const startMoment = moment(startDate, periodSelectorDateFormat);
        const startStr = startMoment.startOf('day').format("YYYY-MM-DD");
        const endStr = moment(getViewEndExclusive(startMoment, selectedTab, tilesToShow))
            .subtract(1, 'days')
            .endOf('day')
            .format("YYYY-MM-DD HH:mm:ss");

        return {
            requestCount: perPage,
            pageNumber: currentPageNumber,
            searchTerm: searchTerm,
            sortField: sortedBy === firstColumnLabel ? "AssetIdentifier" : sortedBy,
            sortDirection: sortedDir,
            filters: filters,
            startDate: startStr,
            endDate: endStr
        };
    }

    useEffect(() => {
        if (assetEvents && searchResultData && assetsWithEventWarnings) {
            assetsWithEventWarnings.forEach(assetWarning => {
                const assetSearchData = searchResultData.filter(x => x.key === assetWarning.assetId)[0];

                if (assetSearchData) {
                    assetSearchData.warning = {
                        hasWarnings: assetWarning.warnings.length > 0,
                        hasDangerousWarnings: assetWarning.warnings.some(x => x.dangerous === true),
                        warnings: assetWarning.warnings ? assetWarning.warnings.map(x => x.message) : []
                    }
                }
            });

            const data = searchResultData.map((d, rowIndex) => {
                return [...mapColumnRows(d, rowIndex)];
            });
            setSearchResult(data);
            setIsSearching(false);
        }
    }, [assetEvents, searchResultData, assetsWithEventWarnings, assetsWithNotes]);

    function clearScreenListing() {
        setSearchResult([]);
        setSearchResultData(null);
        setAssetEvents(null);
        setAssetsWithEventWarnings(null);
    }

    function performSearch(forceSearch) {
        const searchPayload = getSearchPayload();
        setLastSearchPayload(searchPayload);

        if (!forceSearch && (!searchPayload ||
            (currentSelectedTab.current === selectedTab &&
                currentPageNo.current === searchPayload.pageNumber &&
                currentSearchTerm.current === searchPayload.searchTerm &&
                currentStartDate.current === searchPayload.startDate &&
                currentTableSortBy.current === searchPayload.sortField &&
                currentTableSortDir.current === searchPayload.sortDirection &&
                currentAppliedFilters.current?.length === appliedFilters.length &&
                !currentAppliedFilters.current.some(x => !appliedFilters.some(y => y.name === x.name && y.value === x.value)) &&
                !appliedFilters.some(x => !currentAppliedFilters.current.some(y => y.name === x.name && y.value === x.value)) &&
                currentLocations.current === selectedLocations.length))
        ) {
            //If none of the values have changed, then 2 have been updated at once or it's the initial load from Redux, so prevent the listing from performing another call.
            return;
        }

        setIsSearching(true);
        //This should ensure the page only loads once
        clearScreenListing();

        if (currentPageNo.current === searchPayload.pageNumber && currentStartDate.current === searchPayload.startDate && currentEndDate.current === searchPayload.endDate) {
            //The reason for the reload isn't the page number or date changing, so we can reset the page number to 1 but we don't want to reload the page when it happens!
            searchPayload.pageNumber = 1;
            currentPageNo.current = 1;
            setCurrentPageNumber(1);
        } else {
            currentPageNo.current = searchPayload.pageNumber;
        }

        currentSelectedTab.current = selectedTab;
        currentSearchTerm.current = searchPayload.searchTerm;
        currentStartDate.current = searchPayload.startDate;
        currentEndDate.current = searchPayload.endDate;
        currentTableSortBy.current = searchPayload.sortField;
        currentTableSortDir.current = searchPayload.sortDirection;
        currentAppliedFilters.current = appliedFilters;
        currentLocations.current = selectedLocations.length;

        FleetService.searchEventAssets(
            searchPayload.startDate,
            searchPayload.endDate,
            searchPayload.requestCount,
            searchPayload.pageNumber,
            searchPayload.searchTerm,
            searchPayload.sortField,
            searchPayload.sortDirection,
            searchPayload.filters).then((response) => {

            if (response.totalCount === 0) {
                setSearchResult([]);
                setSearchResultData([]);
                setAssetEvents([]);
                setAssetsWithEventWarnings([]);
                setTotalRecords(response.totalCount);
                return;
            }

            if (response.data != null) {
                const tempSearchResultData = formatColumnDataFromSearchResult(response.data);
                setSearchResultData(tempSearchResultData);
                let assetIds = response.data.map(x => x.assetId);

                getWarningDataForSearchedAssets(assetIds);
                
                const startMoment = moment(startDate, periodSelectorDateFormat);
                const eventsEndExclusive = getViewEndExclusive(startMoment, selectedTab, tilesToShow);
                const startStrForEvents = moment(searchPayload.startDate).format("YYYY-MM-DD");
                
                const endStrForEvents = eventsEndExclusive.format("YYYY-MM-DD");

                getNotesForSearchedAssets(assetIds, startMoment, endStrForEvents);

                FleetService.searchAssetEvents(
                    startStrForEvents,
                    endStrForEvents,
                    assetIds,
                    searchPayload.filters
                ).then(eventData => {
                    setAssetEvents(eventData);
                }).catch(() => {
                    setAssetEvents([]);
                });
            }

            setTotalRecords(response.totalCount);
        }).catch((error) => {
            NotificationToaster.show(Intent.DANGER, `Asset search failed. Please try again. ${error} `);
        });
    }

    function getWarningDataForSearchedAssets(assetIds) {
        FleetService.getAssetsWithEventWarnings(assetIds)
            .then((warningsResponse) => {
                setAssetsWithEventWarnings(warningsResponse);
            })
    }

    function getNotesForSearchedAssets(assetIds, start, end) {
        AssetService.getNotes(assetIds, start, end)
            .then((notesResponse) => {
                setAssetsWithNotes(Array.isArray(notesResponse.assetNotes) ? notesResponse.assetNotes : []);
            })
    }

    function setFleetSearchDispatch(newLocations, newSearchTerm, newSortedBy, newCurrentPageNumber, newSortedDir, newShowFilters, newSelectedTab, newStartDateDayWeek, newStartDateMonth) {
       const startDateDayWeekValue = newStartDateDayWeek ?? startDateDayWeek;
       const startDateMonthValue = newStartDateMonth ?? startDateMonth;
       dispatch(SetFiltering(filterSearchSelector, {
            locations: newLocations ?? selectedLocations,
            searchTerm: newSearchTerm ?? searchTerm,
            sortedBy: newSortedBy ?? sortedBy,
            currentPageNumber: newCurrentPageNumber ?? currentPageNumber,
            sortedDir: newSortedDir ?? sortedDir,
            showFilters: newShowFilters ?? showFilters,
            selectedTab: newSelectedTab ?? selectedTab,
            // Serialize moments — Redux requires serializable state.
            startDateDayWeek: startDateDayWeekValue ? moment(startDateDayWeekValue).toISOString() : null,
            startDateMonth: startDateMonthValue ? moment(startDateMonthValue).toISOString() : null
        }));
    }

    function onSearchTextChange(item) {
        setSearchTerm(item);
        setFleetSearchDispatch(null, item, null, null, null, null);
    }

    function openKeyModal() {
        setShowKeyModal(true);
    }

    function closeKeyModal() {
        setShowKeyModal(false);
    }

    useEffect(() => {
        if (startDate) {
            //currentSelectedTab.current
            if (selectedTab === 2 && startDate !== recalcPeriodWithDateMonth.startDate) {
                setRecalcPeriodWithDateMonth(prev => {
                    return {
                        ...prev,
                        startDate: startDate
                    }
                });
            } else if (selectedTab !== 2 && startDate !== recalcPeriodWithDateWeek.startDate) {
                setRecalcPeriodWithDateWeek(prev => {
                    return {
                        ...prev,
                        startDate: startDate
                    }
                });
            }
        }
    }, [startDate]);

    function onWeekChange(periodStart, periodEnd, periodOffset, doubleClick) {
        if (!periodStart) return;

        const dt = moment(periodStart).startOf('isoWeek');

        if (!startDateDayWeek.isSame(dt)) {
            setStartDateDayWeek(dt);
            setIsSearching(true);
            setStartDateDayWeek(dt);
            let monthStart = dt.clone().startOf('month');
            setRecalcPeriodWithDateMonth(prev => {
                return {
                    ...prev,
                    startDate: monthStart
                }
            });
            setStartDateMonth(monthStart); 
            setFleetSearchDispatch(null, null, null, null, null, null, null, dt, monthStart);
        }
        //This could be the same, but we'll set it to ensure the search attempts to run as the tab could have changed
        if (!doubleClick) {
            setStartDate(dt);
        } else {
            setRecalcPeriodWithDateWeek(prev => {
                return {
                    ...prev,
                    startDate: dt
                }
            });
        }
    }

    function onMonthChange(periodStart) {
        if (!periodStart) return;

        const dt = moment(periodStart).startOf('month');

        if (!startDateMonth.isSame(dt)) {
            setIsSearching(true);
            setStartDateMonth(dt);
            let weekStart = dt.clone().startOf('isoWeek');
            setRecalcPeriodWithDateWeek(prev => {
                return {
                    ...prev,
                    startDate: weekStart
                }
            });
            setStartDateDayWeek(weekStart);
            setFleetSearchDispatch(null, null, null, null, null, null, null, weekStart, dt);
        }
        //This could be the same, but we'll set it to ensure the search attempts to run as the tab could have changed
        setStartDate(dt);
    }

    function onPagingChange(page) {
        setCurrentPageNumber(page);
        setFleetSearchDispatch(null, null, null, page, null, null);
    }

    function onTableSort(header, direction) {
        let sortBy = (typeof header === 'string') ? header : header.props.content;
        setSortedBy(sortBy);
        setSortedDir(direction);
        setFleetSearchDispatch(null, null, sortBy, null, direction, null);
    }

    function openPreferencesModal() {
        setShowPreferencesModal(true);
    }

    function closePreferencesModal() {
        setShowPreferencesModal(false);
    }

    useEffect(() => {
        setTotalPages(Math.ceil(totalRecords / perPage));
    }, [totalRecords, perPage])

    
    // Day Detail Modal
    function displayDayDetailModal(asset, date) {
        setDayDetailModalAsset(asset);
        setDayDetailPeriodDetails({
            periodType: getPeriodType(),
            startDate: date.toString(),
        })
        setShowDayDetailModal(true);
    }

    function hideDayDetailModal() {
        setDayDetailModalAsset({});
        setDayDetailPeriodDetails({});
        setShowDayDetailModal(false);
    }

    function getPeriodType() {
        switch(selectedTab) {
            case 0:
                return "day";
            case 1:
                return "weeks";
            case 2:
                return "months";
            default:
                return "day";
        }
    }

    function onReportDownloading(isDownloading, reportName) {
        if (isDownloading) {
            setReportDownloading(true);
            setDownloadingReportName(reportName);
            if (hideBannerTimeout) {
                clearTimeout(hideBannerTimeout);
                hideBannerTimeout = null;
            }
        } else {
            hideBannerTimeout = setTimeout(() => {
                setReportDownloading(false);
                setDownloadingReportName(null);
                hideBannerTimeout = null;
            }, longMessageMilliseconds);
        }
    }

    return (
        <UserAccess perform={requiredActions}
            yes={() => (
                <>
                    <div className="contained-content">
                        <div className="contained-content-static">

                            <h1>Fleet Planner</h1>


                            <div className="filter-controls spacer-bottom-small">
                                <div className="inline-items">
                                    <div className="inline-item fill">
                                        <div className="form-field-full">
                                            <FormTextInput
                                                inputRef={inputSearchRef}
                                                placeholder="Search Fleet"
                                                onChange={(x) => onSearchTextChange(x.target.value)}
                                                value={searchTerm}
                                                large
                                                disabled={isInitialising}
                                                icon="search"
                                                id="user-search-field"
                                                loading={isInitialising}
                                            />
                                        </div>
                                    </div>
                                    <div className="inline-item">
                                        <FormLocationSelector
                                            businessArea={""}
                                            loading={isInitialising}
                                            onLocationListOpened={onLocationListOpened}
                                            onLocationListClose={onLocationListClose}
                                            selectedLocations={selectedLocations}
                                            setSelectedLocations={onSelectedLocationsChange}
                                            useLocationDefaults={useLocationDefaults}
                                            useHierarchy={useHierarchy}
                                            setUseHierarchy={setUseHierarchy}
                                            locationDefaultsToTrue={useLocationDefaults}
                                            customLocations={locationList}
                                            useCustomLocations={true}
                                            disabled={isInitialising || isSearching}
                                        />
                                    </div>
                                    <div className="inline-item">
                                        <FilterButton onToggle={onToggleFilter} filters={appliedFilters} />
                                    </div>
                                </div>
                                <Filter
                                    filterName={filterName}
                                    visible={showFilters}
                                    filters={pageFilters}
                                    onUpdate={onFilterChange}
                                    onClickOutside={onToggleFilter}
                                    numOfColumns={5}
                                    customRenderers={{
                                        ASSETTYPE: (props) => (
                                            <AssetTypeFilterRenderer
                                                key="asset-Type-filter-renderer"
                                                {...props}
                                                expandedAssetTypes={expandedAssetTypes}
                                                setExpandedAssetTypes={setExpandedAssetTypes}
                                            />
                                        )
                                    }}
                                    loading={isInitialising}
                                    disabled={isInitialising || isSearching}
                                />
                            </div>
                            <div className="inline-items spacer-bottom-small">
                                <div className="inline-item fill">
                                    <div className="inline-items">
                                        <ShowHide
                                            evaluator={selectedTab === 2}
                                            show={(
                                                <FormDatePeriodSelector
                                                    id="listing-period-months"
                                                    periodType="months"
                                                    periodsToShow={null}
                                                    startDate={startDateMonth?.format(periodSelectorDateFormat)}
                                                    onChange={onMonthChange}
                                                    dateFormat={periodSelectorDateFormat}
                                                    showCalendar={true}
                                                    showText={false}
                                                    resetDate={moment().startOf("month").format(periodSelectorDateFormat)}
                                                    loading={isInitialising}
                                                    disabled={isInitialising || isSearching}
                                                    key="month-period-selector"
                                                    recalcPeriodWithDate={recalcPeriodWithDateMonth}
                                                />
                                            )}
                                            hide={(
                                                <FormDatePeriodSelector
                                                    id="listing-period-weeks"
                                                    periodType="weeks"
                                                    periodsToShow={null}
                                                    startDate={startDateDayWeek?.format(periodSelectorDateFormat) }
                                                    onChange={onWeekChange}
                                                    dateFormat={periodSelectorDateFormat}
                                                    showCalendar={true}
                                                    showText={false}
                                                    resetDate={moment().startOf("isoWeek").format(periodSelectorDateFormat)}
                                                    loading={isInitialising}
                                                    disabled={isInitialising || isSearching}
                                                    key="week-period-selector"
                                                    recalcPeriodWithDate={recalcPeriodWithDateWeek}
                                                />
                                            )}
                                        />
                                        <div>
                                            <Tabs
                                                tabs={tabSettings}
                                                onClick={onTabClick}
                                                loading={isSearching || isInitialising}
                                                disabled={isInitialising || isSearching}
                                                variant="pill"
                                            />
                                        </div>
                                        <div className="inline-divide"></div>
                                        <div className="button-row">
                                            <ShowHide
                                                evaluator={canAccessCreateAsset}
                                                show={( 
                                                    <Tooltip content="Add new asset">
                                                        <LinkButton intent="primary" 
                                                            large={false} 
                                                            icon="plus" 
                                                            iconOnly 
                                                            href="/asset/create?area=MMF" 
                                                            id="asset-create" 
                                                            loading={isInitialising}
                                                            disabled={isInitialising || isSearching}
                                                        />
                                                    </Tooltip>
                                                )}
                                            />
                                            <Tooltip content="Reports">
                                                <ReportList
                                                    compact
                                                    lastSearchPayload={lastSearchPayload}
                                                    onReportDownloadingChange={onReportDownloading}
                                                    pageName="FMS"
                                                    large={false}
                                                    loading={isInitialising}
                                                    disabled={isInitialising || isSearching}
                                                />
                                            </Tooltip>
                                            <Tooltip content="Key">
                                                <Button intent="secondary" 
                                                    icon="properties" 
                                                    iconOnly 
                                                    id="key" 
                                                    onClick={openKeyModal} 
                                                    className="key-button"
                                                    large={false} 
                                                    loading={isInitialising}
                                                    disabled={isInitialising || isSearching}
                                                />
                                            </Tooltip>
                                        </div>
                                        <div className="inline-divide"></div>
                                        <LinkButton
                                            text="fleet overview"
                                            intent="primary"
                                            href="/fleet/overview"
                                            loading={isInitialising}
                                            disabled={isInitialising || isSearching}
                                            large={false}
                                        />
                                        <div className="inline-divide"></div>
                                        <div className="button-row">
                                            <Button text="Column Preferences" minimal={true} large={false} intent="secondary" icon="cog" id="configure-properties" className="configure-prefs-button" onClick={openPreferencesModal}
                                                loading={isInitialising}
                                                disabled={isInitialising || isSearching} />
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <ListingPagination currentPage={currentPageNumber} totalPages={totalPages} onPageChange={onPagingChange} loadingData={isSearching || isInitialising} className="pagination-inline" />
                                </div>
                            </div>
                            <NotificationInline
                                intent="success"
                                text={
                                    downloadingReportName === "Fleet List Extract"
                                        ? (
                                            <span>
                                                Your report has been queued, and will download as soon as it's ready.<br />
                                                Note that only filters relating to assets and locations will be applied.
                                            </span>
                                        )
                                        : "Your report has been queued, and will download as soon as it's ready."
                                }
                                show={reportDownloading}
                            />
                            <div ref={refHeaderTable}>
                                <ListingTable
                                    id="listing-table-vehicle-headers"
                                    headers={activeHeaders}
                                    data={[]}
                                    totalRecordCount={1}
                                    loadingData={false}
                                    sortable
                                    sortableHeaders={sortableHeaders}
                                    sortedBy={sortedBy}
                                    sortedDir={sortedDir}
                                    onSort={onTableSort}
                                    columnClasses={columnClasses}
                                    columnWidths={columnWidths}
                                    loadingClass={searchResult.length === 0 ? "bp6-skeleton" : "bp6-skeleton loading"}
                                    pageable={false}
                                    fitToContent
                                />
                            </div>

                        </div>

                        <div className={classNames(
                            "contained-content-auto fleet-main-view",
                            {
                                "fleet-month-view": selectedTab === 2,
                                "fleet-week-view": selectedTab === 1,
                                "fleet-day-view": selectedTab === 0
                            }
                        )} ref={refOverflowTable}>
                            <ListingTable
                                id="listing-table-fleet"
                                headers={[]}
                                data={searchResult}
                                totalRecordCount={totalRecords}
                                onPagingChange={onPagingChange}
                                loadingData={isSearching || isInitialising}
                                noDataHeading="No Assets Found"
                                noDataMessage="No assets for this search criteria"
                                sortable
                                sortableHeaders={sortableHeaders}
                                sortedBy={sortedBy}
                                sortedDir={sortedDir}
                                onSort={onTableSort}
                                columnClasses={columnClasses}
                                columnWidths={columnWidths}
                                pageable={false}
                                fitToContent
                            />
                        </div>
                    </div>

                    <ShowHide
                        evaluator={showKeyModal}
                        show={(
                            <KeyModal
                                showKeyModal={showKeyModal}
                                closeKeyModal={closeKeyModal}
                                isInitialising={isInitialising}
                                screenKey="MMF"
                                ownershipForKeyModal={ownershipAttributeMappings}
                                assetStatusesForKeyModal={assetStatusesMappings}
                                eventStatusesKeyModal={eventStatusesMappings}
                                eventTypesForKeyModal={eventTypeMappings}
                            />
                        )}
                    />

                    <ShowHide
                        evaluator={showDayDetailModal}
                        show={(
                            <FleetDayDetailModal
                                showDayDetailModal={showDayDetailModal}
                                assetDetails={{
                                    assetId: dayDetailModalAsset?.key,
                                    assetLocation: {
                                        assetStatus: { id: dayDetailModalAsset?.status?.id },
                                        assetOwnership: { id: dayDetailModalAsset?.ownership?.id },
                                    },
                                    registration: dayDetailModalAsset?.registrationNumber
                                }}
                                periodDetails={dayDetailPeriodDetails}
                                closeDayDetailModal={hideDayDetailModal}
                                mappedAssetOptions={{
                                    ownershipAttributeMappings: ownershipAttributeMappings,
                                    assetStatusesMappings: assetStatusesMappings
                                }}
                                accountEventTypes={accountEventTypes}
                                eventOptions={eventOptions}
                                key={dayDetailModalAsset.key + dayDetailPeriodDetails.startDate}
                                onEventsChanged={() => performSearch(true)}
                                assetProfileArea="MMF"
                            />
                        )}
                    />

                    <ShowHide
                        evaluator={showPreferencesModal}
                        show={(
                            <PreferencesModal
                                isOpen={showPreferencesModal}
                                onClose={closePreferencesModal}
                                displayPreferences={displayPreferences}
                                numberOfWeeks={timeUnitsToShow}
                                cancelButtonIntent={Intent.NONE}
                                onSave={handleSavePreferences}
                                onSavePreferences={FleetService.saveDisplayPreferences}
                                onPreferencesSaved={() => setPreferencesUpdated(true)}
                                preferencesKey="Fleet"
                            />
                        )}
                    />
                </>
            )}
            no={() => <PageRestricted />}
        />
    );
}